<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import bg from '@/assets/img/case/case_bg.svg'
import bgMin from '@/assets/img/case/case_bg--min.svg'
import bgElem1 from '@/assets/img/hero/hero_bg-elem-3.png'
import { getPdfs } from '@/api/docs'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import { useInViewport } from '@/composable/useInViewport'

const caseSectionRef = ref(null)
const presentation = ref('')
const categoryAnimationStarted = ref(false)
const caseAnimationStarted = ref(false)
const hoveredIndex = ref(null)
const swiperRef = ref(null)
const isMobile = ref(window.innerWidth < 992)
const visibleSlideCount = ref(1)
const randomHoverIndex = ref(null)

const prevEl = ref(null)
const nextEl = ref(null)

const bgElems = [{ src: bgElem1, cx: 100, cy: 100, a: 50 }]

const props = defineProps({
	items: { type: Object, required: true },
	activeCategorySlug: { type: String, default: null },
	loading: { type: Boolean, default: false },
})

const emit = defineEmits(['category-click'])

const fileName = computed(() => {
	if (!presentation.value) return ''
	const parts = presentation.value.split('/')
	return parts[parts.length - 1] || ''
})

const filteredCases = computed(() => props.items?.cases || [])

function chunkArray(array, size) {
	const result = []
	for (let i = 0; i < array.length; i += size) {
		result.push(array.slice(i, i + size))
	}
	return result
}

const chunkedSlides = computed(() => chunkArray(filteredCases.value, 6))
const totalSlides = computed(() => chunkedSlides.value.length)
const showMoreButton = computed(() => isMobile.value && visibleSlideCount.value < totalSlides.value)

const handleResize = () => {
	isMobile.value = window.innerWidth < 992
	visibleSlideCount.value = 1
}

const handleShowMore = () => {
	if (visibleSlideCount.value < totalSlides.value) {
		visibleSlideCount.value += 1
	}
}

function onSwiper(swiper) {
	nextTick(() => {
		swiper.params.navigation.prevEl = prevEl.value
		swiper.params.navigation.nextEl = nextEl.value
		swiper.navigation.init()
		swiper.navigation.update()
	})
}

const { isIntersecting } = useInViewport(caseSectionRef)

watch(
	() => props.items.categories,
	(newCategories) => {
		if (props.activeCategorySlug || !newCategories?.length) return
		const allCategory = newCategories.find((cat) => cat.slug === 'all')
		if (allCategory) emit('category-click', allCategory.slug)
	},
	{ immediate: true },
)

watch(isIntersecting, (visible) => {
	if (visible && !categoryAnimationStarted.value) {
		setTimeout(() => {
			categoryAnimationStarted.value = true
			caseAnimationStarted.value = true
		}, 100)
	}
})

watch(
	() => [...(props.items.cases || [])],
	async () => {
		await nextTick()
		swiperRef.value?.swiper?.update()
	},
	{ deep: true, immediate: true },
)

onMounted(async () => {
	try {
		const { data } = await getPdfs()
		presentation.value = data.presentation || ''
	} catch (err) {
		console.warn('Ошибка при загрузке PDF:', err)
	}

	randomHoverIndex.value = Math.floor(Math.random() * 6) + 1
	window.addEventListener('resize', handleResize)
	handleResize()
})

onMounted(() => {
	nextTick(() => {
		if (swiperRef.value?.swiper) {
			swiperRef.value.swiper.navigation.init()
			swiperRef.value.swiper.navigation.update()
		}
	})
})

onBeforeUnmount(() => {
	window.removeEventListener('resize', handleResize)
})
</script>

<template>
	<section class="case" id="case" ref="caseSectionRef">
		<div class="case__bg">
			<picture>
				<source :srcset="bg" media="(min-width: 991.98px)" />
				<img :src="bgMin" alt="" />
			</picture>
		</div>

		<div
			v-for="(image, index) in bgElems"
			:key="index"
			:class="`case__bg-elem case__bg-elem-${index + 1}`"
			v-parallax
			:data-prlx-cx="image.cx"
			:data-prlx-cy="image.cy"
			:data-prlx-a="image.a"
			:data-prlx-dxr="image.dxr ? '' : null"
			:data-prlx-dyr="image.dyr ? '' : null"
		>
			<img :src="image.src" alt="" />
		</div>

		<div class="case__container">
			<div class="case__block">
				<div class="section-header">
					<h2>Кейсы</h2>
					<nav class="case__navigation">
						<button
							v-for="(tab, index) in items.categories"
							:key="tab.slug"
							type="button"
							:class="[
								'case__title',
								{
									'_tab-active': activeCategorySlug === tab.slug,
									_disabled: loading,
									'case__title--animated': categoryAnimationStarted,
								},
							]"
							:style="{
								transitionDelay: categoryAnimationStarted
									? `${index * 50}ms`
									: '0ms',
							}"
							@click="
								!loading &&
								activeCategorySlug !== tab.slug &&
								$emit('category-click', tab.slug)
							"
							:disabled="loading"
						>
							{{ tab.name }}
						</button>
					</nav>
				</div>

				<div class="case__wrapper">
					<div class="case__content">
						<div v-if="loading" class="case__loading">
							<img
								width="36"
								height="36"
								src="@/assets/img/icons/loader_white.svg"
								alt=""
							/>
						</div>

						<div v-else class="case__body">
							<Swiper
								v-if="!isMobile"
								ref="swiperRef"
								class="case__slider"
								:modules="[Navigation]"
								:slides-per-view="1"
								:space-between="750"
								:navigation="{
									prevEl: prevEl,
									nextEl: nextEl,
								}"
								@swiper="onSwiper"
							>
								<SwiperSlide
									v-for="(group, slideIndex) in chunkedSlides"
									:key="slideIndex"
									:class="[
										'case__slide',
										hoveredIndex !== null
											? `hover-${hoveredIndex + 1}`
											: `hover-${randomHoverIndex}`,
									]"
								>
									<div
										v-for="(item, index) in group"
										:key="item.id"
										:class="[
											'case__item',
											{ 'case__item--animated': caseAnimationStarted },
										]"
										:style="{
											transitionDelay: caseAnimationStarted
												? `${index * 50}ms`
												: '0ms',
										}"
										@mouseenter="hoveredIndex = index"
										@mouseleave="hoveredIndex = null"
									>
										<router-link
											class="case__link"
											:to="{
												name: 'Cases',
												query: {
													category: activeCategorySlug,
													id: item.id,
												},
											}"
										>
											<div class="case__img">
												<img :src="item.image_main" :alt="item.title" />
											</div>
											<div class="case__desc">
												<h4 class="dont-break-out">{{ item.title }}</h4>
												<h5>{{ item.awards }}</h5>
											</div>
										</router-link>
									</div>
								</SwiperSlide>
							</Swiper>

							<div v-else class="case__slider case__slider--mobile">
								<div
									v-for="(group, slideIndex) in chunkedSlides"
									:key="slideIndex"
									class="case__slide"
									v-show="slideIndex < visibleSlideCount"
								>
									<div
										v-for="(item, index) in group"
										:key="item.id"
										:class="[
											'case__item',
											{ 'case__item--animated': caseAnimationStarted },
										]"
										:style="{
											transitionDelay: caseAnimationStarted
												? `${index * 50}ms`
												: '0ms',
										}"
										@mouseenter="hoveredIndex = index"
										@mouseleave="hoveredIndex = null"
									>
										<router-link
											class="case__link"
											:to="{ name: 'Cases', query: { id: item.id } }"
										>
											<div class="case__img">
												<img :src="item.image_main" :alt="item.title" />
											</div>
											<div class="case__desc">
												<h4 class="dont-break-out">{{ item.title }}</h4>
												<h5>{{ item.awards }}</h5>
											</div>
										</router-link>
									</div>
								</div>
							</div>

							<div class="case__btn" v-if="showMoreButton">
								<button
									type="button"
									class="btn _btn-second"
									@click="handleShowMore"
								>
									<div class="btn__text">Смотреть еще</div>
									<svg
										class="btn__shadow"
										preserveAspectRatio="none"
										viewBox="0 0 320 72"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path d="M10 72L0 62H310L320 72L10 72Z" fill="#818B8F" />
									</svg>
								</button>
							</div>
						</div>
					</div>

					<div class="case__action" v-if="!isMobile">
						<a
							class="case__download"
							rel="nofollow noopener"
							target="_blank"
							:href="presentation"
							:download="fileName"
						>
							<svg
								width="12"
								height="18"
								viewBox="0 0 12 18"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M6 0.5V13.7222M6 13.7222L1 8.68518M6 13.7222L11 8.68518M1 17.5H11"
									stroke="currentColor"
								/>
							</svg>
							Скачать презентацию
						</a>
						<div class="case__buttons">
							<button ref="prevEl" class="arrow-btn case__button-prev" type="button">
								<div class="arrow-btn__icon">
									<svg
										viewBox="0 0 100 100"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M62.8328 83.5L28.5 49.5074L61.8379 16.5L71.7657 26.3294L48.0956 49.7655L72.5 73.9287L62.8328 83.5Z"
											fill="#F27699"
										/>
									</svg>
								</div>

								<svg
									class="arrow-btn__shadow"
									preserveAspectRatio="none"
									viewBox="0 0 130 126"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path d="M10 126L0 116.308H120L130 126H10Z" fill="#818B8F" />
									<path
										d="M130 9.70376L120 0L120 116.308L130 126L130 9.70376Z"
										fill="#3A3E40"
									/>
								</svg>
							</button>
							<button ref="nextEl" class="arrow-btn case__button-next" type="button">
								<div class="arrow-btn__icon">
									<svg
										viewBox="0 0 100 100"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M38.1671 16.5L72.5 50.4926L39.1621 83.5L29.2344 73.6706L52.9044 50.2345L28.5 26.0713L38.1671 16.5Z"
											fill="#F27699"
										/>
									</svg>
								</div>

								<svg
									class="arrow-btn__shadow"
									preserveAspectRatio="none"
									viewBox="0 0 130 126"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path d="M10 126L0 116.308H120L130 126H10Z" fill="#818B8F" />
									<path
										d="M130 9.70376L120 0L120 116.308L130 126L130 9.70376Z"
										fill="#3A3E40"
									/>
								</svg>
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<style scoped lang="scss">
.case {
	position: relative;
	padding: 9rem 0 0;
	margin-top: 1.5rem;

	@media (min-width: 991.98px) {
		height: 100.4rem;
		overflow: hidden;
	}

	@media (max-width: 991.98px) {
		padding: 3.6rem 0 5.9rem;
	}

	@media (min-width: 991.98px) {
		.section-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
		}
	}

	// .case__bg
	&__bg {
		position: absolute;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		z-index: -1;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	&__bg-elem {
		position: absolute;
		z-index: 10;
		will-change: transform;
		transition: transform 0.2s ease-out;
		pointer-events: none;

		&-1 {
			@media (min-width: 991.98px) {
				width: 18.9rem;
				height: 19rem;
				top: 31.7rem;
				right: 10.3rem;
			}

			@media (max-width: 991.98px) {
				width: 11.7rem;
				height: 11.7rem;
				bottom: -7rem;
				right: 0.6rem;
			}
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
		}
	}

	// .case__container
	&__container {
		height: 100%;
	}

	&__block {
		position: relative;
		height: 100%;
	}

	// .case__navigation
	&__navigation {
		display: flex;
		gap: 1.2rem;

		@media (min-width: 991.98px) {
			margin-right: 14.7rem;
		}

		@media (max-width: 991.98px) {
			padding: 4rem 2rem 2rem;
			margin: -2rem;
			overflow-y: hidden;
			overflow-x: auto;

			&::-webkit-scrollbar {
				width: 0;
				height: 0;
			}
		}
	}

	&__title {
		padding: 1.9rem 2.3rem;
		font-weight: 300;
		font-size: 1.8rem;
		// line-height: 3.2rem;
		line-height: 108%;
		text-transform: uppercase;
		background-color: #2b2e2f;
		transition: background-color 0.3s ease 0s;
		transition:
			all 0.5s ease,
			opacity 0.5s ease,
			transform 0.5s ease;
		opacity: 0;
		transform: translateX(-100%);

		&--animated {
			opacity: 1;
			transform: translateX(0);
		}

		@media (max-width: 991.98px) {
			font-size: 1.8rem;
			padding: 1.5rem 2.3rem;
			flex: 0 0 auto;
			white-space: nowrap;
		}

		&._tab-active {
			background-color: #f06793;
		}

		@media (any-hover: hover) {
			&:hover {
				background-color: #f06793;
			}
		}
	}

	// .case__wrapper
	&__wrapper {
		padding: 3.4rem 0 6.5rem;
		position: relative;

		@media (min-width: 991.98px) {
			height: calc(24rem * 3);
		}

		@media (max-width: 991.98px) {
			padding: 4.4rem 0 0;
		}

		@media (min-width: 991.98px) {
			&::after {
				content: '';
				position: absolute;
				width: 100vw;
				height: 12rem;
				z-index: -1;
				background-color: #f06793;
				top: 46.5%;
				left: 50%;
				transform: translate(-50%, -50%);
			}
		}
	}

	// .case__content
	&__content {
	}

	// .case__body
	&__body {
	}

	// .case__slider
	&__slider {
		overflow: visible;
		width: 100%;
		position: relative;
		margin: 0 auto;
		display: block;

		@media (min-width: 991.98px) {
			height: calc(24rem * 3);
			max-width: 120rem;
		}
	}

	&__slide {
		@media (max-width: 991.98px) {
			max-width: 50rem;
			display: grid;
			grid-template-columns: repeat(2, 1fr);
		}
	}

	// .case__item
	&__item {
		width: 24rem;
		height: 24rem;
		aspect-ratio: 1 / 1;
		transition:
			all 0.5s ease,
			opacity 0.5s ease,
			transform 0.5s ease;
		z-index: 1;
		overflow: hidden;
		position: relative;
		opacity: 0;
		transform: translateX(-100%);

		&--animated {
			opacity: 1;
			transform: translateX(0);
		}

		@media (min-width: 991.98px) {
			position: absolute;
		}

		@media (max-width: 991.98px) {
			width: auto;
			height: auto;

			// &:nth-child(6n + 1) {
			// 	grid-column: 1/2;
			// 	grid-row: 1/2;
			// }
			// &:nth-child(6n + 2) {
			// 	grid-column: 2/3;
			// 	grid-row: 1/2;
			// }
			// &:nth-child(6n + 3) {
			// 	grid-column: 2/3;
			// 	grid-row: 2/3;
			// }
			// &:nth-child(6n + 4) {
			// 	grid-column: 1/3;
			// 	grid-row: 3/5;
			// }
			// &:nth-child(6n + 5) {
			// 	grid-column: 1/2;
			// 	grid-row: 5/6;
			// }
			// &:nth-child(6n + 6) {
			// 	grid-column: 2/3;
			// 	grid-row: 6/7;
			// }
		}

		&::before {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			z-index: 1;
			pointer-events: none;
			background-color: #fff;
			transition: all 0.3s ease 0s;
		}

		&:nth-child(1)::before {
			background: rgba(210, 45, 79, 0.8);
		}
		&:nth-child(2)::before {
			background: rgba(209, 23, 15, 0.8);
		}
		&:nth-child(3)::before {
			background: rgba(223, 158, 38, 0.8);
		}
		&:nth-child(4)::before {
			background: rgba(29, 55, 108, 0.8);
		}
		&:nth-child(5)::before {
			background: rgba(29, 55, 108, 0.8);
		}
		&:nth-child(6)::before {
			background: rgba(209, 23, 15, 0.8);
		}

		//========================================================================================================================================================

		@media (min-width: 991.98px) {
			&:nth-child(1) {
				top: 24rem;
				left: 0rem;
			}
			&:nth-child(2) {
				top: 24rem;
				left: 24rem;
			}
			&:nth-child(3) {
				top: 24rem;
				left: 48rem;
				width: 24rem;
				height: 24rem;
			}
			&:nth-child(4) {
				top: 24rem;
				left: 96rem;
			}
			&:nth-child(5) {
				top: 48rem;
				left: 24rem;
			}
			&:nth-child(6) {
				top: 48rem;
				left: 72rem;
			}
		}

		//========================================================================================================================================================
		@media (min-width: 991.98px) {
			.hover-1.swiper-slide-active & {
				&:nth-child(1) {
					top: 0rem;
					left: 0rem;
					width: 48rem;
					height: 48rem;
				}
				&:nth-child(2) {
					top: 24rem;
					left: 48rem;
				}
				&:nth-child(3) {
					top: 24rem;
					left: 72rem;
					width: 24rem;
					height: 24rem;
				}
			}

			.hover-2.swiper-slide-active & {
				&:nth-child(2) {
					top: 0rem;
					left: 24rem;
					width: 48rem;
					height: 48rem;
				}
				&:nth-child(3) {
					top: 24rem;
					left: 72rem;
					width: 24rem;
					height: 24rem;
				}
			}

			.hover-3.swiper-slide-active & {
				&:nth-child(3) {
					top: 0rem;
					left: 48rem;
					width: 48rem;
					height: 48rem;
				}
			}

			.hover-4.swiper-slide-active & {
				&:nth-child(4) {
					top: 0rem;
					left: 72rem;
					width: 48rem;
					height: 48rem;
				}
				&:nth-child(3) {
					top: 24rem;
					left: 48rem;
					width: 24rem;
					height: 24rem;
				}
			}

			.hover-5.swiper-slide-active & {
				&:nth-child(5) {
					top: 24rem;
					left: 0rem;
					width: 48rem;
					height: 48rem;
				}
				&:nth-child(1) {
					top: 0rem;
				}
				&:nth-child(2) {
					top: 0rem;
				}
			}

			.hover-6.swiper-slide-active & {
				&:nth-child(6) {
					top: 24rem;
					left: 48rem;
					width: 48rem;
					height: 48rem;
				}
				&:nth-child(3) {
					top: 0rem;
					left: 72rem;
					width: 24rem;
					height: 24rem;
				}
			}

			@media (any-hover: hover) {
				&:hover {
					&::before {
						opacity: 0;
						visibility: hidden;
					}

					.case__desc {
						opacity: 0;
						visibility: hidden;
					}
				}
			}
		}
	}

	// .case__link
	&__link {
		display: block;
		width: 100%;
		height: 100%;
	}

	// .case__img
	&__img {
		width: 100%;
		height: 100%;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	// .case__desc
	&__desc {
		position: absolute;
		padding: 2rem 2.4rem;
		top: 0;
		left: 0;
		z-index: 2;
		transition: all 0.3s ease 0s;

		@media (max-width: 991.98px) {
			padding: 1.2rem 1.6rem;
		}

		h5 {
			margin-top: 0.8rem;
		}
	}

	// .case__action
	&__action {
		position: absolute;
		bottom: 0;
		right: 0;
		z-index: 10;

		@media (max-width: 991.98px) {
			display: none;
		}
	}

	&__buttons {
		display: flex;
		gap: 2rem;
	}

	// .case__download
	&__download {
		margin-bottom: 3rem;
		font-weight: 300;
		font-size: 2.2rem;
		// line-height: 2.4rem;
		line-height: 110%;
		letter-spacing: 0.02em;
		max-width: 18.7rem;
		display: flex;
		align-items: center;
		gap: 1.8rem;
		transition: color 0.3s ease 0s;

		@media (any-hover: hover) {
			&:hover {
				color: var(--primary-color);
			}
		}

		svg {
			flex-shrink: 0;
		}
	}

	&__btn {
		width: 100%;
		margin-top: 2.7rem;

		@media (min-width: 991.98px) {
			display: none;
		}

		.btn {
			width: 100%;
		}
	}

	&__loading {
		position: absolute;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		z-index: 10;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 36px;
			animation: animationLoader 0.7s linear infinite;
		}

		@keyframes animationLoader {
			0% {
				transform: rotate(0deg);
			}
			100% {
				transform: rotate(360deg);
			}
		}
	}
}

@media (max-width: 991.98px) {
	.case__slider--mobile {
		display: block !important;
	}
}
</style>
